import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Show, SignInButton } from "@clerk/react";
import { Trophy, ChevronRight } from "lucide-react";
import { AlertsToggle } from "@/components/AlertsToggle";
import { fmtDate, slug, useApi, weekendOf } from "@/lib/api";
import { raceMs, clock12 } from "@/lib/raceFormat";
import { WeekendPass } from "@/components/WeekendPass";
import { SilksIcon } from "@/components/SilksIcon";
import { TipPrice } from "@/components/TipPrice";
import { TipSheets } from "@/components/sheet/TipSheets";

const TipRow = ({ t, i }) => (
  <Link to={`/meeting/${t.meeting_id}?race=${t.race_number}`} data-testid={`tip-row-${slug(t.track)}-${t.race_number}`} style={{ animationDelay: `${i * 50}ms` }}
    className="rise group block surface p-4 sm:p-5 hover:border-blue-500/50 transition-colors">
    <div className="flex items-start gap-3">
      <div className="text-center shrink-0 w-20">
        <p className="font-mono font-extrabold text-base whitespace-nowrap">{clock12(raceMs(t, t.date)) || "--:--"}</p>
        <p className="text-[10px] uppercase tracking-widest text-slate-500">Brisbane</p>
      </div>
      <div className="min-w-0 flex-1">
        <p className="eyebrow truncate">{t.state} · {t.track} R{t.race_number}{t.distance ? ` · ${t.distance}m` : ""}</p>
        <p className="font-display font-bold text-slate-100 truncate">{t.race_name}</p>
        {t.locked && <p className="mt-2 text-sm text-amber-300" data-testid={`tip-locked-${slug(t.track)}-${t.race_number}`}>{t.count} selection(s) · weekend pass needed</p>}
        <ol className="mt-3 pt-3 border-t border-[#202E4C] space-y-2.5">
          {t.selections.map((s, j) => (
            <li key={j} className="flex items-start gap-3 text-base">
              <span data-testid={`tip-rank-${slug(t.track)}-${t.race_number}-${j + 1}`} className={`w-8 h-8 shrink-0 rounded-full grid place-items-center text-base font-extrabold ${j === 0 ? "bg-amber-400 text-slate-950" : "bg-[#1B2640] border border-blue-500/40 text-blue-300"}`}>{j + 1}</span>
              <SilksIcon url={s.silk_url} colours={s.silk_colours} testid={`tip-silk-${slug(t.track)}-${t.race_number}-${j + 1}`} />
              <span className="min-w-0">
                <span className="block font-bold text-slate-100 truncate">{s.number}. {s.horse}</span>
                <TipPrice s={s} testid={`tip-price-${slug(t.track)}-${t.race_number}-${j + 1}`} />
                {s.note && <span className="text-xs text-slate-400 leading-snug line-clamp-2">{s.note}</span>}
              </span>
            </li>
          ))}
        </ol>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-blue-400 group-hover:translate-x-1 transition-transform" />
    </div>
  </Link>
);

export default function Tips() {
  const [data, setData] = useState(null);
  const [date, setDate] = useState("");
  const [err, setErr] = useState("");
  const [reload, setReload] = useState(0);
  const api = useApi();
  const locked = data?.tips.find((t) => t.locked);
  useEffect(() => {
    setErr("");
    api("get", `/tips${date ? `?date=${date}` : ""}`).then(setData).catch(() => setErr("Couldn't load tips right now. Please try again shortly."));
  }, [date, api, reload]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <p className="eyebrow mb-2">The Weekend Edge</p>
      <h1 className="page-title mb-2" data-testid="tips-title">{!date ? "Today's Tips" : "Tips"}</h1>
      <p className="text-slate-400 mb-6">Every approved selection for {data ? fmtDate(data.date) : "today"}, in jump order.</p>
      <div className="mb-6">
        <Show when="signed-in"><AlertsToggle /></Show>
        <Show when="signed-out">
          <SignInButton><button data-testid="tips-signin-alerts" className="text-sm text-blue-400 hover:underline">Sign in to get an email when tips are published</button></SignInButton>
        </Show>
      </div>
      {data?.dates?.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
          {data.dates.map((d) => (
            <button key={d} data-testid={`tips-date-${d}`} onClick={() => setDate(d)}
              className={`pill-btn ${data.date === d ? "pill-on" : "pill-off"}`}>{fmtDate(d)}</button>
          ))}
        </div>
      )}
      {locked && <div className="mb-6"><WeekendPass weekend={weekendOf(data.date)} count={data.tips.reduce((n, t) => n + (t.count || 0), 0)} onUnlocked={() => setReload((x) => x + 1)} /></div>}
      {data && <TipSheets date={data.date} reload={reload} />}
      {err && <p className="text-rose-300" data-testid="tips-error">{err}</p>}
      {!data && !err && <p className="text-slate-400">Loading…</p>}
      {data && data.tips.length === 0 && (
        <div data-testid="tips-empty" className="rounded-xl border border-dashed border-[#202E4C] p-10 text-center text-slate-400">
          <Trophy className="w-6 h-6 mx-auto mb-2" />No tips published for {fmtDate(data.date)} yet.
        </div>
      )}
      <div className="space-y-3" data-testid="tips-list">{data?.tips.map((t, i) => <TipRow key={`${t.meeting_id}-${t.race_number}`} t={t} i={i} />)}</div>
      <p className="text-[11px] text-slate-500 mt-8">Opinion only, never a guarantee. Gamble responsibly — call 1800 858 858.</p>
    </div>
  );
}
